import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { showUpgradeExternally, LEAVE_PLAYER_EVENT } from "../components/UpgradePrompt";
import { showAgeGateExternally } from "../lib/ageGateBus";
import { getTvScreenHost } from "../lib/tvScreen";
import { askForLogin } from "../lib/askLogin";
import { toast } from "sonner";
import { clearFiles, sample, SAMPLE_SECONDS } from "../lib/myFiles";

const PlayerContext = createContext(null);

// Every song that starts is counted on the site, except a video from the member's own device (My Videos): the site does not know it.
const countPlay = (v) => { if (v && !v.local) api.post(`/videos/${v.id}/play`).catch(() => {}); };

// What a free member sees when they press Autoplay or Shuffle and the song is not playing from a playlist or Favorites.
const PLAYLIST_ONLY = "These features only work in playlists and favorites.";

// Prompt the correct upgrade modal when a locked (premium/featured) video is
// unplayable or its preview has finished.
//   - Guest  → "Create a free login" with CTA → /register
//   - Free   → "Sign up for Premium" with CTA → /account?checkout=1 (the Account page
//              sends them straight on to Stripe's checkout page)
function _promptForLocked(video, user) {
  const isGuest = !user;
  if (isGuest) {
    showUpgradeExternally("Create a free login", {
      ctaLabel: "Create a free login",
      ctaPath: "/register",
      body: "Sign up to keep watching full tracks, build playlists, and save favorites.",
    });
  } else {
    showUpgradeExternally("Sign up for Premium", {
      ctaLabel: "Sign up for Premium",
      ctaPath: "/account?checkout=1",
    });
  }
}

// The end of the 10 second try of one of their own videos (My Videos): a guest is asked to create a login, a free member to go Premium.
function _promptForMyVideos(user) {
  if (!user) {
    showUpgradeExternally("Create a free login", {
      ctaLabel: "Create a free login",
      ctaPath: "/register",
      body: "That was a 10 second sample. Create a free login, then go Premium to play up to 200 of your own videos in full.",
    });
  } else {
    showUpgradeExternally("Sign up for Premium", {
      ctaLabel: "Sign up for Premium",
      ctaPath: "/account?checkout=1",
      body: "That was a 10 second sample. Premium members play up to 200 of their own videos in full, with the lights, Shuffle and Repeat.",
    });
  }
}

// An explicit (18+) song whose links were held back because this account has not passed the age check.
//   - Guest        → the same "Create a free login" prompt as any locked song
//   - Logged in    → the age check pop-up (components/AgeGate.js)
const isAgeLocked = (v) => !!(v && v.age_locked);
function _promptForAge(video, user) {
  if (!user) { _promptForLocked(video, user); return; }
  showAgeGateExternally(video);
}

// Persisted playback settings (per-browser, per-user device)
const LS_AUTOPLAY = "dtm_autoplay";
const LS_SHUFFLE = "dtm_shuffle";
const LS_REPEAT = "dtm_repeat";
const readBool = (key, def) => {
  const v = typeof window !== "undefined" ? localStorage.getItem(key) : null;
  return v === null ? def : v === "true";
};

export const PlayerProvider = ({ children }) => {
  const { user, ready } = useAuth();
  // Same premium check used elsewhere (e.g. VideoCard heart button).
  const canUsePremium = !!user && (user.is_premium || user.role === "admin");

  const videoRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // "On the TV": the song was started from the TV on the About page, so its picture plays inside the TV screen and the big player
  // stays closed (the small player bar at the bottom has the buttons). It only counts while that TV is on the page.
  const [inlineTv, setInlineTv] = useState(false);
  // True while the queue is a playlist the member started (Playlists page, or Favorites on the Favorites page or the About TV). For a free
  // member Autoplay and Shuffle only work then; anything else (browsing, search, a genre, one song) plays that song and stops.
  // Premium members and admins can use both on every page.
  const [fromPlaylist, setFromPlaylist] = useState(false);
  const inlineRef = useRef(false);
  const onTv = useCallback(() => inlineRef.current && !!getTvScreenHost(), []);

  // Raw (saved) playback settings — default: autoplay ON, shuffle OFF, repeat OFF — persisted in localStorage.
  // Autoplay next, Shuffle and Previous/Next are free for everyone with a login (free, Premium and admin). A guest can use
  // Repeat; Autoplay, Shuffle, Previous and Next ask a guest to create a free login. A guest's saved choices are kept, so
  // they come back once the person logs in.
  const [rawAutoplay, setRawAutoplay] = useState(() => readBool(LS_AUTOPLAY, true));
  const [rawShuffle, setRawShuffle] = useState(() => readBool(LS_SHUFFLE, false));
  const [rawRepeat, setRawRepeat] = useState(() => readBool(LS_REPEAT, false));
  useEffect(() => { localStorage.setItem(LS_AUTOPLAY, String(rawAutoplay)); }, [rawAutoplay]);
  useEffect(() => { localStorage.setItem(LS_SHUFFLE, String(rawShuffle)); }, [rawShuffle]);
  useEffect(() => { localStorage.setItem(LS_REPEAT, String(rawRepeat)); }, [rawRepeat]);

  // The sign-up / upgrade pop-up is about to open another page: close the big player so that page can be seen.
  // The song stays in the small player bar.
  useEffect(() => {
    const leave = () => setExpanded(false);
    window.addEventListener(LEAVE_PLAYER_EVENT, leave);
    return () => window.removeEventListener(LEAVE_PLAYER_EVENT, leave);
  }, []);

  // The settings in force. Autoplay and Shuffle need a login, and (unless Premium) a playlist or Favorites playing; Repeat works for everyone.
  const apOpen = canUsePremium || fromPlaylist;
  const autoplay = !!user && apOpen && rawAutoplay;
  const shuffle = !!user && apOpen && rawShuffle;
  const repeat = rawRepeat;
  const setAutoplay = useCallback((on) => {
    if (!user) { askForLogin("autoplay"); return; }
    if (!apOpen) { toast(PLAYLIST_ONLY, { id: "playlist-only" }); return; }
    setRawAutoplay(on);
  }, [user, apOpen]);
  const setShuffle = useCallback((on) => {
    if (!user) { askForLogin("shuffle"); return; }
    if (!apOpen) { toast(PLAYLIST_ONLY, { id: "playlist-only" }); return; }
    setRawShuffle(on);
  }, [user, apOpen]);

  // Play history for Shuffle: remembers the songs played before the current one
  // so Previous can step back through them (including songs skipped with Next).
  const historyRef = useRef([]);

  // When the signed-in person changes (log out, or log in as someone else) stop the music and
  // empty the player. The songs in the queue were handed out under the old account, so a
  // premium member's full-length song must not keep playing for a guest or a free member.
  const loadedKeyRef = useRef(null);
  const lastWhoRef = useRef(undefined);   // undefined = we have not seen who is signed in yet
  useEffect(() => {
    if (!ready) return;
    const who = user ? user.id : null;
    if (lastWhoRef.current === undefined) { lastWhoRef.current = who; return; }   // first look, nothing to stop
    if (lastWhoRef.current === who) return;                                       // same person (e.g. account refreshed)
    lastWhoRef.current = who;
    clearFiles();     // the videos picked on My Videos belong to the person who picked them
    const v = videoRef.current;
    if (v) {
      try { v.pause(); } catch { /* ignore */ }
      try { v.removeAttribute("src"); v.load(); } catch { /* ignore */ }
    }
    loadedKeyRef.current = null;
    historyRef.current = [];
    inlineRef.current = false;
    setInlineTv(false);
    setPlaying(false);
    setExpanded(false);
    setQueue([]);
    setIndex(-1);
    setCurrent(null);
    setProgress(0);
    setDuration(0);
  }, [ready, user]);

  const playQueue = useCallback((videos, startIdx = 0, opts) => {
    const target = videos[startIdx];
    if (!target) return;
    // Explicit song, age check not passed yet: ask for the login / the age check and play nothing.
    if (isAgeLocked(target)) {
      _promptForAge(target, user);
      return;
    }
    // Locked video with no preview: show the sign-up/upgrade prompt and
    // play nothing. (Preview exists → fall through to normal playback.)
    if (target.locked && !target.video_url) {
      _promptForLocked(target, user);
      return;
    }
    historyRef.current = [];
    // opts.inline: play it inside the TV screen on the About page (only when that TV is on the page)
    const inline = !!(opts && opts.inline) && !!getTvScreenHost();
    inlineRef.current = inline;
    setInlineTv(inline);
    setFromPlaylist(!!(opts && opts.playlist));
    setQueue(videos);
    setIndex(startIdx);
    setCurrent(target);
    setPlaying(true);
    setExpanded(!inline);
    countPlay(target);
  }, [user]);

  const playOne = useCallback((video) => playQueue([video], 0), [playQueue]);

  // --- End-of-list wrap behaviour for the manual Next/Previous buttons -----
  // Separate from the Repeat setting (which still controls what happens when
  // a song finishes on its own in handleEnded). Rules:
  //   - Next on last song wraps to the first.
  //   - Previous on first song wraps around to the last song.
  //   - If the list has only one song, both Next and Previous restart it.
  // A guest (not logged in) pressing Next or Previous gets the "Create a free login" box instead.
  // With Shuffle ON (logged in):
  //   - Next jumps to a random OTHER song and remembers where it came from.
  //   - Previous goes back to the song played before (not random), stepping
  //     back through the history. With no history it falls back to the
  //     normal behaviour above.
  // ---------------------------------------------------------------------------
  const restartCurrent = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      try { v.currentTime = 0; } catch { /* ignore */ }
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    }
    setPlaying(true);
    if (!onTv()) setExpanded(true);
  }, [onTv]);

  const next = useCallback(() => {
    if (!user) { askForLogin("skip"); return; }
    if (!queue.length) return;
    if (queue.length === 1) {
      const t = queue[0];
      if (isAgeLocked(t)) { _promptForAge(t, user); return; }
      if (t.locked && !t.video_url) { _promptForLocked(t, user); return; }
      restartCurrent();
      return;
    }
    let i;
    if (shuffle) {
      do { i = Math.floor(Math.random() * queue.length); } while (i === index);
      historyRef.current.push(index);
      if (historyRef.current.length > 200) historyRef.current.shift();
    } else {
      i = (index + 1) % queue.length;
    }
    const target = queue[i];
    setIndex(i);
    setCurrent(target);
    if (!onTv()) setExpanded(true);
    if (isAgeLocked(target)) {
      // Explicit song, age check not passed: advance (a later Next skips past it) but do not play.
      setPlaying(false);
      _promptForAge(target, user);
      return;
    }
    if (target.locked && !target.video_url) {
      // Advance so a subsequent Next can skip past it, but do not play.
      setPlaying(false);
      _promptForLocked(target, user);
      return;
    }
    setPlaying(true);
    countPlay(target);
  }, [queue, index, shuffle, restartCurrent, user, onTv]);

  const prev = useCallback(() => {
    if (!user) { askForLogin("skip"); return; }
    if (!queue.length) return;
    // Shuffle ON: go back to the song that was actually played before this one.
    let i = null;
    if (shuffle) {
      while (historyRef.current.length) {
        const h = historyRef.current.pop();
        if (h !== index && h >= 0 && h < queue.length) { i = h; break; }
      }
    }
    if (i === null) {
      if (queue.length === 1) {
        const t = queue[0] || current;
        if (isAgeLocked(t)) { _promptForAge(t, user); return; }
        if (t && t.locked && !t.video_url) { _promptForLocked(t, user); return; }
        restartCurrent();
        return;
      }
      // First song: wrap around to the last song.
      i = index <= 0 ? queue.length - 1 : index - 1;
    }
    const target = queue[i];
    setIndex(i);
    setCurrent(target);
    if (!onTv()) setExpanded(true);
    if (isAgeLocked(target)) {
      setPlaying(false);
      _promptForAge(target, user);
      return;
    }
    if (target.locked && !target.video_url) {
      setPlaying(false);
      _promptForLocked(target, user);
      return;
    }
    setPlaying(true);
  }, [queue, index, shuffle, restartCurrent, current, user, onTv]);

  const toggle = () => setPlaying((p) => {
    const nextState = !p;
    // When resuming playback (false -> true), also reopen the fullscreen box.
    // Pausing (true -> false) must NOT change expanded.
    if (nextState && !onTv()) setExpanded(true);
    return nextState;
  });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = muted ? 0 : volume;
  }, [volume, muted]);

  // ---------------------------------------------------------------------------
  // Single playback effect — guarantees exactly one play()/pause() per state
  // change and prevents "double play" when both `current` and `playing` update
  // in the same tick (as happens on next/prev/playOne). A ref tracks the id of
  // the source currently loaded in the <video> element; we only re-load when
  // the track actually changed, and we always fully pause+reset before load()
  // + play() so a stale track never bleeds into the new one.
  // ---------------------------------------------------------------------------
  // The key is song id + position in the queue, so a playlist that holds the
  // same song twice (premium) still restarts it when Next lands on the repeat.
  const playTokenRef = useRef(0);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !current) return;
    const trackKey = `${current.id}#${index}`;
    const trackChanged = loadedKeyRef.current !== trackKey;
    const myToken = ++playTokenRef.current;

    if (trackChanged) {
      try { v.pause(); } catch { /* ignore */ }
      try { v.currentTime = 0; } catch { /* ignore */ }
      loadedKeyRef.current = trackKey;
      v.load();
    }

    if (playing) {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          if (myToken === playTokenRef.current) setPlaying(false);
        });
      }
    } else {
      try { v.pause(); } catch { /* ignore */ }
    }
  }, [playing, current, index]);

  const seek = (pct) => {
    const v = videoRef.current;
    if (v && duration) v.currentTime = (pct / 100) * duration;
  };

  // Picks the next index when a song ends on its own (respects shuffle+repeat).
  // Only invoked from handleEnded, and only for people who are logged in.
  const pickEndedNextIndex = useCallback(() => {
    if (!queue.length) return null;
    // Explicit songs still held back for the 18+ check are skipped here, so the music keeps going on its own.
    // (The age check pop-up only appears when someone clicks an explicit song.)
    const ok = (k) => !isAgeLocked(queue[k]);
    if (shuffle) {
      if (queue.length === 1) return repeat ? 0 : null;
      const others = [];
      for (let k = 0; k < queue.length; k++) { if (k !== index && ok(k)) others.push(k); }
      if (!others.length) return null;
      return others[Math.floor(Math.random() * others.length)];
    }
    for (let k = index + 1; k < queue.length; k++) { if (ok(k)) return k; }
    if (repeat) { for (let k = 0; k < queue.length; k++) { if (ok(k)) return k; } }
    return null;
  }, [queue, index, shuffle, repeat]);

  const handleEnded = useCallback(() => {
    // Preview clip finished for a locked (premium/featured) video on a guest
    // or free user: stop playback and show the appropriate sign-up prompt.
    if (current && current.locked) {
      setPlaying(false);
      _promptForLocked(current, user);
      return;
    }
    // A guest who watched the demo song on the About page's TV: the "Create a free login" box comes up when it ends
    // (Dan asked for that). Everywhere else a regular song just ends: guests play and browse songs freely, no pop-up.
    if (!user && onTv()) {
      setPlaying(false);
      showUpgradeExternally("Create a free login", {
        ctaLabel: "Create a free login",
        ctaPath: "/register",
        body: "Sign up to keep playlists, favorites, and continuous playback.",
      });
      return;
    }
    // Repeat plays the same song again when there is nothing to go on to: a one-song queue, or Autoplay off
    // (a guest never has Autoplay, so Repeat is what keeps a guest's song going).
    if (repeat && (!autoplay || queue.length === 1)) {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
      return;
    }
    // Autoplay next off: the song just ends.
    if (!autoplay) { setPlaying(false); return; }
    const i = pickEndedNextIndex();
    if (i === null) { setPlaying(false); return; }
    // Remember where we came from so Previous can go back (Shuffle only).
    if (shuffle && i !== index) {
      historyRef.current.push(index);
      if (historyRef.current.length > 200) historyRef.current.shift();
    }
    setIndex(i);
    setCurrent(queue[i]);
    setPlaying(true);
    countPlay(queue[i]);
  }, [current, user, onTv, autoplay, repeat, shuffle, index, queue, pickEndedNextIndex]);

  return (
    <PlayerContext.Provider
      value={{
        videoRef, queue, index, current, playing, progress, duration, volume, muted, expanded,
        // The settings in force (a guest has no Autoplay and no Shuffle).
        autoplay, shuffle, repeat,
        // canUsePremium is for Premium-only things (Favorites); isGuest dims the two buttons that need a free login.
        canUsePremium, isGuest: !user, inlineTv, fromPlaylist, apOpen,
        playQueue, playOne, next, prev, toggle,
        setPlaying, setExpanded,
        // For a guest, setAutoplay and setShuffle show the "Create a free login" box and change nothing.
        setAutoplay, setShuffle, setRepeat: setRawRepeat,
        setMuted, setVolume, seek,
        onTimeUpdate: (e) => {
          const t = e.target.currentTime;
          setProgress(t);
          // A guest or free member may try one of their own videos for 10 seconds (My Videos): cut it off there and ask.
          if (current && current.local && !canUsePremium && !e.target.paused) {
            if (!sample.id) sample.id = current.id;
            if (sample.id !== current.id || sample.done || t >= SAMPLE_SECONDS) {
              sample.done = true;
              try { e.target.pause(); } catch { /* ignore */ }
              setPlaying(false);
              _promptForMyVideos(user);
            }
          }
        },
        onLoadedMetadata: (e) => setDuration(e.target.duration),
        onEnded: () => {
          // a video shorter than 10 seconds is the whole sample: the same question when it ends
          if (current && current.local && !canUsePremium) {
            if (!sample.id) sample.id = current.id;
            sample.done = true;
            setPlaying(false);
            _promptForMyVideos(user);
            return;
          }
          handleEnded();
        },
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
